import React, { useRef, useState } from "react";
import { Box, Button, Flex, Image, Input, Spinner, Text } from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import { uploadImageApi } from "../api/attendance.api";
import { extractErrorMessage } from "../api/client";
import { resolveImageUrl } from "../utils/format";
import { EMP_API_BASE_URL } from "../config";
import { appImages } from "../assets/appicons";

const PRIMARY = "#4081EB";
const GRAD = "linear(to-r, #68A2FF, #2168DA)";

// value: string[] (filePath/URL đã đồng bộ). onChange(next: string[])
// Luồng khớp app: chọn ảnh -> preview tạm -> bấm "Tải ảnh lên" mới đồng bộ.
const ImageUploader = ({ value = [], onChange, max = 5 }) => {
  const libRef = useRef(null);
  const camRef = useRef(null);
  const [mode, setMode] = useState("upload"); // upload | link
  const [pending, setPending] = useState([]); // [{ file, url }]
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);

  const room = max - value.length - pending.length;

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (room <= 0) {
      toast.error(`Tối đa ${max} ảnh`);
      return;
    }
    const picked = files
      .slice(0, room)
      .map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setPending((p) => [...p, ...picked]);
  };

  const removePending = (i) => setPending((p) => p.filter((_, idx) => idx !== i));
  const removeValue = (i) => onChange?.(value.filter((_, idx) => idx !== i));

  const doUpload = async () => {
    if (!pending.length) return;
    setUploading(true);
    try {
      const results = [];
      for (const it of pending) {
        // eslint-disable-next-line no-await-in-loop
        const data = await uploadImageApi(it.file);
        const path = typeof data === "string" ? data : data?.filePath;
        if (path) results.push(path);
      }
      onChange?.([...value, ...results]);
      setPending([]);
      toast.success("Đã tải ảnh lên");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Tải ảnh thất bại"));
    } finally {
      setUploading(false);
    }
  };

  const addLink = () => {
    const v = link.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      toast.error("Đường dẫn ảnh không hợp lệ");
      return;
    }
    if (value.length >= max) {
      toast.error(`Tối đa ${max} ảnh`);
      return;
    }
    onChange?.([...value, v]);
    setLink("");
  };

  const Thumb = ({ src, onRemove }) => (
    <Box position="relative" w="84px" h="84px">
      <Image src={src} alt="" w="100%" h="100%" objectFit="cover" borderRadius="8px" border="1px solid #DAE0EB" />
      <Flex
        as="button"
        type="button"
        onClick={onRemove}
        position="absolute"
        top="-8px"
        right="-8px"
        w="22px"
        h="22px"
        borderRadius="full"
        bg="#EF2E14"
        color="white"
        align="center"
        justify="center"
      >
        <FiX />
      </Flex>
    </Box>
  );

  return (
    <Box>
      {/* Segmented toggle */}
      <Flex bg="#EEF2F7" borderRadius="8px" p="3px" mb="10px">
        {[
          { k: "upload", label: "Tải ảnh từ thiết bị" },
          { k: "link", label: "Sử dụng đường dẫn có sẵn" },
        ].map((s) => {
          const on = mode === s.k;
          return (
            <Flex
              key={s.k}
              as="button"
              type="button"
              flex="1"
              h="34px"
              align="center"
              justify="center"
              borderRadius="6px"
              bgImage={on ? GRAD : undefined}
              onClick={() => setMode(s.k)}
            >
              <Text fontSize="13px" fontWeight={600} color={on ? "white" : "#6B7280"}>
                {s.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      <Text fontSize="12px" color={PRIMARY} mb="10px">
        ( Sau khi lựa chọn xong ảnh, bắt buộc phải ấn tải ảnh lên để hệ thống đồng bộ )
      </Text>

      {mode === "upload" ? (
        <>
          <Flex
            border="1px dashed #C9D4E5"
            borderRadius="12px"
            minH="140px"
            align="center"
            justify="space-around"
            px="10px"
            py="20px"
          >
            <Flex as="button" type="button" direction="column" align="center" gap="12px" onClick={() => libRef.current?.click()}>
              <img src={appImages.icon_upload_image} alt="" width={40} height={40} style={{ objectFit: "contain", opacity: 0.85 }} />
              <Text fontSize="13px" color="#6B7280">Tải ảnh của bạn lên</Text>
            </Flex>
            <Flex as="button" type="button" direction="column" align="center" gap="12px" onClick={() => camRef.current?.click()}>
              <img src={appImages.icon_upload_photo} alt="" width={40} height={40} style={{ objectFit: "contain", opacity: 0.85 }} />
              <Text fontSize="13px" color="#6B7280">Chụp ảnh của bạn lên</Text>
            </Flex>
          </Flex>

          {(value.length > 0 || pending.length > 0) && (
            <Flex gap="10px" flexWrap="wrap" mt="12px">
              {value.map((path, idx) => (
                <Thumb key={`v-${idx}`} src={resolveImageUrl(path, EMP_API_BASE_URL)} onRemove={() => removeValue(idx)} />
              ))}
              {pending.map((it, idx) => (
                <Thumb key={`p-${idx}`} src={it.url} onRemove={() => removePending(idx)} />
              ))}
            </Flex>
          )}

          {pending.length > 0 && (
            <Button
              mt="12px"
              w="100%"
              h="44px"
              bgImage={GRAD}
              color="white"
              borderRadius="10px"
              _hover={{ bgImage: GRAD, opacity: 0.92 }}
              leftIcon={uploading ? <Spinner size="sm" /> : undefined}
              onClick={doUpload}
              isDisabled={uploading}
            >
              Tải ảnh lên ({pending.length})
            </Button>
          )}

          <input ref={libRef} type="file" accept="image/*" multiple hidden onChange={onPick} />
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
        </>
      ) : (
        <>
          <Flex gap="8px">
            <Input
              size="sm"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Dán đường dẫn ảnh (https://...)"
              borderColor="#DAE0EB"
              borderRadius="8px"
              fontSize="14px"
            />
            <Button size="sm" px="16px" bgImage={GRAD} color="white" borderRadius="8px" _hover={{ bgImage: GRAD, opacity: 0.92 }} onClick={addLink}>
              Thêm
            </Button>
          </Flex>
          {value.length > 0 && (
            <Flex gap="10px" flexWrap="wrap" mt="12px">
              {value.map((path, idx) => (
                <Thumb key={`lv-${idx}`} src={resolveImageUrl(path, EMP_API_BASE_URL)} onRemove={() => removeValue(idx)} />
              ))}
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};

export default ImageUploader;
